import { ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'
import { type FieldError, useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Button, TelephoneInput, TextInput, TextLink, Tooltip, usePageTitle } from 'slab'
import { handleGrapeAPIFieldErrors } from 'utils'
import { usePostEmployersEmployers as usePostEmployers } from 'utils/web_api'

import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { usePrefill } from '../PrefillProvider'
import { useAPIErrorBanner } from '../useErrorBanner'

export function BasicInfoPage () {
  usePageTitle('Tell us about yourself')

  const { employer, setEmployerId } = useEmployer()
  const { navigateToNextStep } = useOnboardingStep()
  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not create your employer profile because of an error',
  })
  const { getPrefillValue } = usePrefill()

  const form = useForm({
    defaultValues: {
      admin: {
        first_name: getPrefillValue('first_name') ?? '',
        last_name: getPrefillValue('last_name') ?? '',
        job_title: getPrefillValue('job_title') ?? '',
      },
      name: getPrefillValue('company_name') ?? '',
      dba: getPrefillValue('dba') ?? '',
      phone_number: getPrefillValue('phone_number') ?? '',
    },
    mode: 'onTouched',
  })

  const employerRegistrationMutation = usePostEmployers({
    mutation: {
      onSuccess: (data) => {
        setEmployerId(data.id)
        navigateToNextStep()
      },
      onError: (error: GrapeAPIErrorType) => {
        if (error.fieldErrors) {
          handleGrapeAPIFieldErrors(error, form)
        } else {
          setError(error)
        }
      },
    },
  })

  /**
   * If the user somehow lands on this page and is already authenticated as an
   * employer admin, redirect them to the setup page.
   */
  useEffect(() => {
    if (employer) {
      navigateToNextStep()
    }
  }, [employer])

  function errorFor (field: Parameters<typeof form.register>[0]) {
    const path = field.split('.')

    const error = path.reduce((acc, curr) => {
      // @ts-expect-error: This is too much of a pain to type. Just trust that it's correct!
      return acc && acc[curr]
    }, form.formState.errors) as FieldError | undefined

    return error?.message || null
  }

  function onSubmit () {
    const data = form.getValues()

    employerRegistrationMutation.mutate({
      data: {
        admin: data.admin,
        dba: data.dba,
        name: data.name,
        phone_number: data.phone_number,
        create_prospect: true,
        onboarding_experience: 'v2',
      },
    })
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header heading="You're in!" subheading='Tell us a little more about yourself and your company.' />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <div className='space-y-4'>
            <TextInput
              {...form.register('admin.first_name', { required: true })}
              label='First name'
              error={errorFor('admin.first_name')}
              autoFocus
            />
            <TextInput
              {...form.register('admin.last_name', { required: true })}
              label='Last name'
              error={errorFor('admin.last_name')}
            />
            <div className='grid grid-cols-1 xs:grid-cols-2 gap-4'>
              <TextInput
                {...form.register('name', { required: true })}
                label='Company name'
                error={errorFor('name')}
              />
              <TextInput
                {...form.register('dba', { required: false })}
                label='DBA (if applicable)'
                error={errorFor('dba')}
              />
            </div>
            <TextInput
              {...form.register('admin.job_title', { required: true })}
              label='Your job title'
              error={errorFor('admin.job_title')}
            />
            <TelephoneInput
              {...form.register('phone_number', { required: false })}
              label='Mobile phone (optional)'
              placeholder='(xxx) xxx-xxxx'
              error={errorFor('phone_number')}
              description={
                <Tooltip
                  side='bottom'
                  content="We'll use this to provide support and answer questions during enrollment"
                >
                  <TextLink>What's this for?</TextLink>
                </Tooltip>
}
            />
          </div>
          {errorBanner}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button
            type='submit'
            variant='primary'
            rightIcon={ArrowRightIcon}
            disabled={
              employerRegistrationMutation.isPending || !form.formState.isValid
            }
          >
            That's me
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
